import type { TemplateResult } from "lit";
import { LitElement, html, css } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { localized } from "@lit/localize";

import "@wh-web-front-end/wh-component-library/dist/elements/wh-separator";
import type { PrivacyContent } from "../types/content";

@localized()
@customElement("web-kiosk-privacy-content")
export class WebKioskPrivacyContentElement extends LitElement {
  @property() content: PrivacyContent | undefined = undefined;

  @property({ type: String }) active = "";

  @query("#privacy-header") privacyHeader!: HTMLElement;

  @query("#terms-header") termsHeader!: HTMLElement;

  @query("#privacy-sidebar-item") privacySidebar!: HTMLElement;

  @query("#terms-sidebar-item") termsSidebar!: HTMLElement;

  PRIVACY_HEADER_ID = "privacy-header";

  PRIVACY_SIDEBAR_ID = "privacy-sidebar-item";

  TERMS_HEADER_ID = "terms-header";

  TERMS_SIDEBAR_ID = "terms-sidebar-item";

  scrollCallback: IntersectionObserverCallback = (entries) => {
    entries.forEach((entry) => {
      const { id } = entry.target;
      if (entry.isIntersecting) {
        if (id === this.PRIVACY_HEADER_ID) {
          this.setPrivacyActive();
        }
        if (id === this.TERMS_HEADER_ID) {
          this.setTermsActive();
        }
      }
    });
  };

  setPrivacyActive() {
    this.privacySidebar.classList.add("active");
    this.termsSidebar.classList.remove("active");
  }

  setTermsActive() {
    this.termsSidebar.classList.add("active");
    this.privacySidebar.classList.remove("active");
  }

  firstUpdated() {
    const intersectionObserver: IntersectionObserver = new IntersectionObserver(
      this.scrollCallback,
      {
        rootMargin: "5px",
        threshold: 0.05,
      }
    );

    intersectionObserver.observe(this.privacyHeader);
    intersectionObserver.observe(this.termsHeader);
  }

  scrollToPrivacy() {
    this.privacyHeader.scrollIntoView({
      behavior: "smooth",
    });
  }

  scrollToTerms() {
    this.termsHeader.scrollIntoView({
      behavior: "smooth",
    });
  }

  render(): TemplateResult {
    return html`<div class="privacy-text-container">
      <div class="privacy-sidebar">
        <button @click=${this.scrollToPrivacy} class="sidebar-item">
          <img
            src=${this.content?.contentPrivacyIcon}
            alt=${this.content?.contentPrivacyIconAlt}
            class="sidebar-icon"
          />
          <div id=${this.PRIVACY_SIDEBAR_ID}>${
      this.content?.contentPrivacySidebarText
    }</div>
        </button>
        <wh-separator class="sidebar-separator"></wh-separator>
        <button @click=${this.scrollToTerms} class="sidebar-item">
          <img
          src=${this.content?.contentTermsIcon}
            alt=${this.content?.contentTermsIconAlt}
            class="sidebar-icon"
          />
          <div id=${this.TERMS_SIDEBAR_ID}>${
      this.content?.contentTermsSidebarText
    }</div>
        </button>
      </div>

        <div class="privacy-text">
          <div id=${this.PRIVACY_HEADER_ID} class="section-header">
            ${unsafeHTML(this.content?.contentPrivacyBody)}
          </div>
          <div id=${this.TERMS_HEADER_ID} class="section-header">
          ${unsafeHTML(this.content?.contentTermsBody)}
          </div>
        </div>
        <web-kiosk-back-button></web-kiosk-back-button>
      </div>
    </div>`;
  }

  /* Stryker disable next-line StringLiteral */
  static styles = css`
    :host {
      --web-kiosk-font: "Source Sans Pro", helvetica, arial;
      --web-kiosk-font-size-large: 28px;
      --web-kiosk-font-size: 24px;
      --web-kiosk-text-color: #323264;
      --web-kiosk-background-color: #f5f5f0;
    }

    h3 {
      color: var(--web-kiosk-text-color, blue);
      font-size: 48px;
      margin-bottom: 64px;
    }

    h4 {
      color: var(--web-kiosk-text-color, blue);
      font-size: 32px;
      margin-bottom: 64px;
    }

    strong {
      color: var(--web-kiosk-text-color, blue);
      margin-bottom: 64px;
    }

    p {
      margin-bottom: 64px;
    }

    .active {
      font-weight: 700;
    }

    .privacy-sidebar {
      position: sticky;
      top: 224px;
      align-self: flex-start;
      flex: 1 1 1;
      padding: 16px;
    }

    .sidebar-item {
      display: flex;
      align-items: center;
      text-decoration: none;
      cursor: pointer;
      width: 100%;
      height: 100%;
      font-family: var(--web-kiosk-font);
      font-size: var(--web-kiosk-font-size-large);
      color: var(--web-kiosk-text-color, blue);
      text-align: left;
      background-color: transparent;
      border: none;
    }

    .sidebar-separator {
      padding-top: 64px;
      padding-bottom: 64px;
    }

    .sidebar-icon {
      padding-right: 20px;
      padding-left: 20px;
    }

    .privacy-text-container {
      display: grid;
      grid-template-columns: 3fr 8fr 1fr;
      padding: 64px 56px;
      background-color: var(--web-kiosk-background-color, grey);
    }

    .section-header {
      scroll-margin-top: 160px;
    }

    .privacy-text {
      height: 100%;
      font-family: arial, sans-serif;
      font-size: var(--web-kiosk-font-size, 24px);
    }

    web-kiosk-back-button {
      position: fixed;
      bottom: 120px;
      right: 64px;
    }
  `;
}
